import { useState, useEffect, useRef } from "react";
import ProductHeader from "../../Components/ProductsPages/ProductHeader";
import ProductsHeroSection from "../../Components/ProductsPages/ProductsHeroSection";
import {
  keyfeatures,
} from "../../Data/ProductPages/Data";
import ProductFooter from "../../Components/ProductsPages/ProductFooter";
import { FeatureHover } from "../../Components/ProductsPages/FeatureHover";
import RequestDemo from "../../Components/ProductsPages/RequestDemo";
import QSleeveImage from "../../Images/videothumbnail.jpg";
import BackgroundGradient from "../../Images/BackgroundGradient.png";
import BackgroundGradientSmall from "../../Images/BackgroundGradientSmall.png";
import VideoComponent from "../../Components/VideoComponent";
import TitleContent from "../../Components/TitleContent";
import { KeyFeatures, KeyFeaturesCard } from "../../Components/KeyFeatures";
import { ProductTable } from "../../Components/ProductsPages/ProductTable";
import QSENSING from "../../../src/Images/Boxes/QSENSING_P1.png";
import QSENSING_P2 from "../../../src/Images/Boxes/QSENSING_P2.png";

export default function QBiosensor() {
  const requestDemoRef = useRef(null);
  const architectureRef = useRef(null);

  const [backgroundImage, setBackgroundImage] = useState(``);

  useEffect(() => {
    const updateBackgroundImage = () => {
      if (window.innerWidth < 640) {
        setBackgroundImage(`url(${BackgroundGradientSmall})`);
      } else {
        setBackgroundImage(`url(${BackgroundGradient})`);
      }
    };

    updateBackgroundImage();

    window.addEventListener("resize", updateBackgroundImage);

    return () => window.removeEventListener("resize", updateBackgroundImage);
  }, []);

  const scrollToRequestDemo = () => {
    requestDemoRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const scrollToArchitecture = () => {
    architectureRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const keyFeatures = [
    "Cardiac muscle contractions and blood pumping in the heart",
    "Neural firing and synchronization in the brain",
    "Retinal responses to visual stimuli",
    "Electrical activity in muscles, nerves, and other organs",
    "Bulky, expensive systems",
    "Large Dewar enclosures that force sensors ~1.5–2 cm stand-off away from the body",
    "Reduced spatial resolution and weakened signal strength due to standoff distance",
    "Limited portability and high operational costs",
    "Room-temperature operation — no cooling needed",
    "Compact, lightweight sensors — enabling placement directly against the skin or scalp",
    "Higher spatial resolution — closer proximity captures stronger, clearer signals",
    "Enhanced sensitivity and signal-to-noise ratio — ideal for weak biomagnetic fields",
    "Motion tolerance and wearability — paving the way for dynamic, real-time monitoring",
    "Cost-effective and scalable — democratizing access to advanced diagnostics",
  ];

  const specifications = [
    ["S. No.",
    "Operating Parameters",
    "Values/Range (typical) "],
    ["1.",
    "Wavelength",
    "795 nm"],
    ["2.",
    "Responsivity",
    "~ µT (unshielded environment) > nT (shielded environment) "],
    ["3.",
    "Sensitivity",
    "nT- pT depending upon background field "],
    ["4.",
    "Dynamic Range ",
    "± 200 nT (Shielded) ± 50 µT (Unshielded) "],
    ["5.",
    "Atomic Vapor Used temperature",
    "Rubidium, ~30 0C"],
    ["6.",
    "Dimensions",
    "150*75*75 mm , 250 gms"],
  ];

  return (
    <div className="w-full  flex flex-col gap-0 items-center justify-center bg-primary7">
      <div
        className="w-full width flex flex-col gap-0 items-center justify-center bg-contain bg-repeat"
        style={{ backgroundImage: backgroundImage }}
      >
        <ProductHeader scrollToRequestDemo={scrollToRequestDemo} />
        <div className="w-full h-[25rem] ">
          <ProductsHeroSection
            title1="Ultra-Sensitive Quantum"
            title2="Biosensing with OPM Technology"
            content="Detecting femtotesla-level biomagnetic signals with compact, room-temperature sensors for clinical and research applications. "
            scrollToArchitecture={scrollToArchitecture}
          />
        </div>

        <div className="w-full bg-primary7  width md:px-10  flex flex-col items-center justify-center pb-10 gap-1">
          <div className="w-[90%] text-center md:text-left text-[1.5rem] md:text-[2rem] 2xl:text-[2.3rem] font-RobotoBold text-white leading-tight px-3 ">
            <span className="wordGradient">
              Quantum Biosensor - Why OPMs Outperform Traditional Technologies
            </span>
          </div>

          <div className="w-[90%] h-full  grid grid-cols-1 xl:grid-cols-2  place-items-start p-3">
            <div className="w-full  flex flex-col items-center justify-center  gap-1">
              <div className="w-full text-white text-left font-RobotoRegular text-xs md:text-sm px-3">
                A quantum biosensor based on Optically Pumped Magnetometers
                (OPMs) is being developed to detect ultra-weak magnetic fields
                generated by biological processes in the human body. These
                biomagnetic signals, ranging from nano-Tesla (nT) to pico-Tesla
                (pT) and approaching femtotesla levels, arise from ionic
                currents associated with essential physiological activities.
              </div>
              <div className="w-full text-white text-left font-RobotoRegular text-xs md:text-sm px-3">
                By harnessing the quantum properties of alkali atoms (such as
                rubidium or cesium), our OPMs use laser-based optical pumping to
                achieve extraordinary sensitivity often in the femtotesla (fT)
                range at room temperature, without any cryogenic requirements.
              </div>
              <div className="w-full sm:px-10 flex items-start justify-start">
                <KeyFeatures features={keyFeatures.slice(0, 4)} />
              </div>
              <div className="w-full text-white text-left font-RobotoRegular text-xs md:text-sm px-3">
                Conventional biomagnetic sensing relies on Superconducting
                Quantum Interference Devices (SQUIDs), which offer high
                sensitivity but demand liquid helium cryogenic cooling. This
                results in:
              </div>
              <div className="w-full sm:px-10 flex items-start justify-start">
                <KeyFeatures features={keyFeatures.slice(4, 8)} />
              </div>

              {/* <img
                className="mt-3 w-[300px] h-[233px]  object-contain rounded-lg md:rounded-2xl"
                src={require("../../../src/Images/Boxes/ECDL1_BOX.png")}
              ></img> */}
            </div>

            <div className="w-full  flex flex-col items-center justify-center  gap-1">
              <div className="w-full text-white text-left font-RobotoRegular text-xs md:text-sm px-3">
                In contrast, QAIG&#39;s cryogen-free OPMs deliver comparable or
                superior performance with game-changing advantages:
              </div>
              {/* <div className="w-full text-white text-center sm:text-left font-RobotoRegular text-xs md:text-sm px-3">
                By harnessing the quantum properties of alkali atoms (such as
                rubidium or cesium), our OPMs use laser-based optical pumping to
                achieve extraordinary sensitivity often in the femtotesla (fT)
                range at room temperature, without any cryogenic requirements.
              </div> */}
              <div className="w-full  flex flex-col items-center justify-start py-3">
                <KeyFeaturesCard features={keyFeatures.slice(9, 14)} />
              </div>
              <div className="w-full text-white text-left font-RobotoRegular text-xs md:text-sm px-3">
                These features position our OPM-based biosensor to transform
                non-invasive biomagnetic imaging, outperforming SQUIDs in
                practicality, resolution, and affordability.
              </div>

              {/* <img
                className="mt-3 w-[300px] h-[233px]  object-contain rounded-lg md:rounded-2xl"
                src={require("../../../src/Images/Boxes/ECDL1_BOX.png")}
              ></img> */}
            </div>
          </div>
          {/* <div className="w-[90%] h-full flex flex-col justify-center items-center   p-3">
            <div className="w-full text-white text-left font-RobotoRegular text-xs md:text-sm px-3">
              Ideal for cutting-edge research and practical deployments, our
              ECDLs power quantum optics experiments, precision metrology, and
              more.
            </div>
            <div className="w-full text-white text-left font-RobotoRegular text-xs md:text-sm px-3">
              Designed with academia in mind, these lasers serve as the
              centrepiece of our Academia Boxes*—perfect for undergraduate and
              postgraduate teaching laboratories as well. They can also be used
              independently to suit specific experimental needs.
            </div>
          </div> */}
          <div className="w-[90%] bg-primary7  width md:px-10 gap-10 flex flex-col xl:flex-row items-center justify-center pb-20">
            <ProductTable
              title="Key Features of Our Magnetometer"
              data={specifications}
            />
            <div className="px-10 flex flex-col items-center justify-center">
              <img
                src={QSENSING}
                alt="QSENSING"
                className="w-[500px] h-[200px] object-contain rounded-lg md:rounded-2xl"
              />
              <p className="w-full lg:max-w-[600px] text-center text-xs italic text-white/70 mt-2">
                Figure 1: Quantum Sensing Experimentation Kit
              </p>
            </div>
          </div>

          <div className="w-[90%] bg-primary7  width md:px-10  flex flex-col items-center justify-center  pb-20">
            <img
              src={QSENSING_P2}
              alt="QSENSING"
              className="object-contain rounded-lg md:rounded-2xl"
            />
            <p className="w-full lg:max-w-[600px] text-center text-xs italic text-white/70 mt-2">
            Figure 2: Schematic optical layout of Quantum Sensing and Atomic Physics Experimentation Kit
            </p>
          </div>

          <div className="w-[90%] bg-primary7  width md:px-10  flex flex-col items-center justify-center  gap-1">
            {/* <div className="font-RobotoBold  wordGradient text-[1.5rem] md:text-[2rem] 2xl:text-[2.3rem]">
            KEY FEATURES
          </div> */}

            <TitleContent
              title="KEY APPLICATIONS IN CLINICAL AND RESEARCH"
              content="Our quantum biosensor unlocks precise, non-invasive detection of biomarkers for a wide
range of conditions.By enabling earlier diagnosis, continuous personalized monitoring, and broader clinical
deployment, this technology promises to shift paradigms in preventive healthcare and
precision medicine."
            />
            <div className="max-w-5xl  mx-auto px-3 md:px-8">
              <FeatureHover items={keyfeatures.QuantumBiosensor} />
            </div>
          </div>
        </div>

        {/* <VideoComponent
          image={QSleeveImage}
          video={"https://thequantum.ai/videos/QSleeveVideo.mp4"}
        /> */}
        <div
          className="w-full bg-primary7  width flex flex-col items-center justify-center gap-2 "
          ref={requestDemoRef}
        >
          <RequestDemo />
        </div>
        {/* <div className="w-full h-[33rem]">
            <IframeWithPoster
                src="https://www.youtube.com/embed/ldmpb0k5v7E?si=USYWzTZkxH2JxKxk"
                poster={QSleeveImage}
                allow="accelerometer; autoplay; controls; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
            />
        </div> */}
        <ProductFooter />
      </div>
    </div>
  );
}
